import { type Client, createClient } from "graphql-ws";

import { resolveGraphqlEndpoint } from "../lib/resolveGraphqlEndpoint";

const endpoint = resolveGraphqlEndpoint();

export type SubscriptionConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

function getWsUrl(): string {
  const explicit = process.env.REACT_APP_WS_URL?.trim();
  if (explicit) return resolveWsEndpoint(explicit);

  return resolveWsEndpoint(endpoint);
}

function resolveWsEndpoint(graphqlEndpoint: string): string {
  const url = new URL(graphqlEndpoint);
  const normalizedPath = url.pathname.replace(/\/$/, "");
  const isLocalAdminDevHost =
    ["localhost", "127.0.0.1", "::1"].includes(url.hostname) &&
    ["3001", "3004"].includes(url.port);
  const isDirectLocalBackend =
    ["localhost", "127.0.0.1", "::1"].includes(url.hostname) &&
    url.port === "3000";

  if (
    ["/api/graphql", "/graphql"].includes(normalizedPath) &&
    isLocalAdminDevHost
  ) {
    url.hostname = "127.0.0.1";
    url.port = "3000";
    url.pathname = "/graphql";
  }

  if (normalizedPath === "/api/graphql" && isDirectLocalBackend) {
    url.pathname = "/graphql";
  }

  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

let client: Client | null = null;
let connectionState: SubscriptionConnectionState = "idle";
const connectionStateListeners = new Set<
  (state: SubscriptionConnectionState) => void
>();

function setConnectionState(next: SubscriptionConnectionState): void {
  connectionState = next;
  connectionStateListeners.forEach((listener) => listener(next));
}

function getSubscriptionClient(): Client {
  if (!client) {
    client = createClient({
      url: getWsUrl(),
      retryAttempts: 10,
      retryWait: async (retries) => {
        const delayMs = Math.min(1000 * 2 ** Math.min(retries, 4), 10000);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      },
      connectionParams: () => {
        const token = localStorage.getItem("accessToken");
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
      on: {
        connecting: () => setConnectionState("connecting"),
        connected: () => setConnectionState("connected"),
        closed: () => setConnectionState("disconnected"),
        error: () => setConnectionState("error"),
      },
    });
  }
  return client;
}

function subscribeToSubscriptionConnectionState(
  listener: (state: SubscriptionConnectionState) => void,
): () => void {
  connectionStateListeners.add(listener);
  listener(connectionState);
  return () => {
    connectionStateListeners.delete(listener);
  };
}

function getCurrentSubscriptionConnectionState(): SubscriptionConnectionState {
  return connectionState;
}

export {
  getCurrentSubscriptionConnectionState,
  getSubscriptionClient,
  getWsUrl,
  resolveWsEndpoint,
  subscribeToSubscriptionConnectionState,
};
